import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useBranding } from "../lib/branding";
import { withDemoFlag } from "../lib/links";

export default function Footer() {
    const { branding } = useBranding();
    const orgName = branding?.organization_name || "Your Annuity Review";
    const tagline = branding?.tagline || "yourannuityreview.com";
    const disclaimer = branding?.footer_disclaimer || "We do not provide tax or legal advice.";
    const supportEmail = branding?.support_email;
    const supportPhone = branding?.support_phone;

    return (
        <footer className="bg-navy-900 text-white mt-24" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
                <div className="grid md:grid-cols-3 gap-12">
                    <div>
                        <div className="flex items-center gap-3">
                            {branding?.logo_url ? (
                                <img src={branding.logo_url} alt={orgName} className="w-10 h-10 rounded-md object-cover bg-white/10 border border-white/20" />
                            ) : (
                                <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center">
                                    <ShieldCheck className="w-5 h-5" />
                                </div>
                            )}
                            <div>
                                <div className="font-serif text-xl" data-testid="footer-brand-name">{orgName}</div>
                                <div className="uppercase tracking-[0.2em] text-[10px] text-blue-200/70">{tagline}</div>
                            </div>
                        </div>
                        <p className="mt-6 text-blue-100/80 text-base leading-relaxed max-w-sm">
                            We help annuity owners understand what they own — before making any financial decisions. Clarity over pressure.
                        </p>
                        {(supportEmail || supportPhone) && (
                            <div className="mt-6 space-y-1 text-sm text-blue-100/80">
                                {supportEmail && <div data-testid="footer-support-email"><a href={`mailto:${supportEmail}`} className="hover:text-white">{supportEmail}</a></div>}
                                {supportPhone && <div data-testid="footer-support-phone"><a href={`tel:${supportPhone}`} className="hover:text-white">{supportPhone}</a></div>}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="uppercase tracking-[0.2em] text-xs font-semibold text-blue-200/70 mb-4">Quick Links</div>
                        <ul className="space-y-3 text-base">
                            <li><Link to={withDemoFlag("/")} className="hover:text-white text-blue-100/80" data-testid="footer-home">Home</Link></li>
                            <li><Link to={withDemoFlag("/review")} className="hover:text-white text-blue-100/80" data-testid="footer-review">Free Annuity Review</Link></li>
                            <li><Link to={withDemoFlag("/booking")} className="hover:text-white text-blue-100/80" data-testid="footer-booking">Book a Call</Link></li>
                            <li><Link to={withDemoFlag("/about")} className="hover:text-white text-blue-100/80" data-testid="footer-about">About</Link></li>
                        </ul>
                    </div>

                    <div>
                        <div className="uppercase tracking-[0.2em] text-xs font-semibold text-blue-200/70 mb-4">Legal</div>
                        <ul className="space-y-3 text-base">
                            <li><Link to="/privacy" className="hover:text-white text-blue-100/80" data-testid="footer-privacy">Privacy Policy</Link></li>
                            <li><Link to="/terms" className="hover:text-white text-blue-100/80" data-testid="footer-terms">Terms of Service</Link></li>
                        </ul>
                    </div>
                </div>

                <div className="mt-14 pt-8 border-t border-white/10 text-sm text-blue-100/70 leading-relaxed max-w-4xl" data-testid="footer-disclaimer">
                    <strong className="text-white/90">Disclaimer:</strong> {disclaimer}
                </div>

                <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
                    <div className="text-blue-100/50">
                        © {new Date().getFullYear()} {orgName}. All rights reserved.
                    </div>
                    <div className="text-blue-100/60" data-testid="footer-developer-credit">
                        Developed and managed by{" "}
                        <span className="text-white/90 font-medium">Strategic Media Holdings, LLC</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
